import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import Loader from '../reusable/Loader';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Panel from '../reusable/Panel';
import useUserDetails from '../hooks/useUserDetails';
import { getConfiguredSchools, getEOAConfig } from '../../services/eoaConfigServices';
import {
  getSurveyReportWorkflowStatus,
  executeSurveyReportWorkflowTransition,
} from '../../services/surveyReportWorkflowService';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import SurveyReportStatus from './SurveyReportStatus';
import ViewSurveyReportLink from './ViewSurveyReportLink';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import NavArrowFwdIcon from '../assets/nav-arrow-fwd.svg';
import TableChartIcon from '../assets/table-chart.svg';
import './SurveyReportsOverview.css';
import { formatDate } from '../../utils';

interface SchoolWithSurveyData {
  id: string;
  name: string;
  status: string | null;
  dueDate: string;
  surveyDashboardUrl?: string;
  surveyQuestionsUrl?: string;
  availableTransitions: string[];
}

const SurveyReportsOverview = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolWithSurveyData[]>([]);
  const [processingSchoolId, setProcessingSchoolId] = useState<string | null>(null);

  const fetchSchoolsAndStatus = async () => {
    if (!userDetails) return;

    try {
      setLoading(true);

      const eoaConfigResponse = await getEOAConfig(userDetails.eoaId, userDetails.access_token);
      const sjwDate = eoaConfigResponse.data.sjw_date;

      const formattedDueDate = formatDate(sjwDate);

      const schoolsResponse = await getConfiguredSchools(userDetails.eoaId, userDetails.access_token);
      const configuredSchools = schoolsResponse.data;
      const primarySchools = configuredSchools.filter(
        (school: ConfiguredEOASchool) => school.is_primary_on_eoa !== false
      );

      const schoolsWithStatus = await Promise.all(
        primarySchools.map(async (school: ConfiguredEOASchool) => {
          let status: string | null = null;
          let availableTransitions: string[] = [];

          try {
            const statusResponse = await getSurveyReportWorkflowStatus(
              userDetails.eoaId,
              school.id,
              userDetails.access_token
            ).catch(() => ({ current_state: null, available_transitions: [] }));
            status = statusResponse?.current_state ?? null;
            availableTransitions = statusResponse?.available_transitions ?? [];
          } catch {
            // Keep defaults
          }

          return {
            id: school.id,
            name: school.name,
            status,
            dueDate: formattedDueDate,
            surveyDashboardUrl: school.survey_dashboard_url,
            surveyQuestionsUrl: school.survey_questions_url,
            availableTransitions,
          };
        })
      );

      setSchools(schoolsWithStatus);
    } catch (error) {
      console.error('Error fetching schools and status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchoolsAndStatus();
  }, [userDetails]);

  const refreshSchoolWorkflow = async (schoolId: string) => {
    if (!userDetails) return;
    try {
      const statusResponse = await getSurveyReportWorkflowStatus(userDetails.eoaId, schoolId, userDetails.access_token);
      setSchools((prev) =>
        prev.map((school) =>
          school.id === schoolId
            ? {
                ...school,
                status: statusResponse.current_state,
                availableTransitions: statusResponse.available_transitions || [],
              }
            : school
        )
      );
    } catch (error) {
      console.error('Error refreshing school workflow:', error);
    }
  };

  const handleRejectClick = async (schoolId: string) => {
    if (!userDetails) return;
    setProcessingSchoolId(schoolId);
    try {
      await executeSurveyReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        'reject_survey_report'
      );
      await refreshSchoolWorkflow(schoolId);
    } catch (error) {
      console.error('Error rejecting survey report:', error);
    } finally {
      setProcessingSchoolId(null);
    }
  };

  const handleApproveClick = async (schoolId: string) => {
    if (!userDetails) return;
    setProcessingSchoolId(schoolId);
    try {
      await executeSurveyReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        'approve_survey_report'
      );
      await refreshSchoolWorkflow(schoolId);
    } catch (error) {
      console.error('Error approving survey report:', error);
    } finally {
      setProcessingSchoolId(null);
    }
  };

  if (!userDetails) return <Loader />;

  const tableHeaderData: TableHeaderData<SchoolWithSurveyData>[] = [
    { key: 'name', label: 'School', icons: ['sort'] },
    {
      key: 'status',
      label: 'Status',
      icons: ['sort'],
      renderCell: (school) => <SurveyReportStatus status={school.status} size="small" />,
    },
    { key: 'dueDate', label: 'Due date' },
    {
      key: 'actions',
      label: 'Actions',
      renderCell: (school) => {
        const transitions = school.availableTransitions || [];
        const canApprove = transitions.includes('approve_survey_report');
        const canReject = transitions.includes('reject_survey_report');
        const canViewReport =
          school.status === 'SURVEY_REPORT_GENERATED' || school.status === 'SURVEY_REPORT_FINALIZED';
        const isProcessing = processingSchoolId === school.id;
        return (
          <div className="survey-reports-actions">
            {canViewReport && <ViewSurveyReportLink schoolId={school.id} />}
            {canApprove && (
              <CustomButton
                variant="text"
                buttonText="Approve"
                buttonAction={() => handleApproveClick(school.id)}
                disabled={isProcessing}
              />
            )}
            {canReject && (
              <CustomButton
                variant="text"
                buttonText="Reject"
                buttonAction={() => handleRejectClick(school.id)}
                disabled={isProcessing}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'next',
      label: '',
      renderCell: (school) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            aria-label={`Go to survey response dashboard for ${school.name}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/survey-response-dashboard?schoolId=${school.id}`)}
          >
            <img src={NavArrowFwdIcon} alt="" width={24} height={24} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <div className="survey-reports-header">
        <h1 className="survey-reports-page-title">Survey report</h1>
        <div className="survey-reports-header-actions">
          <button
            type="button"
            className="survey-reports-header-link"
            onClick={() => navigate('/survey-response-dashboard')}
          >
            <img src={TableChartIcon} alt="" width={24} height={24} />
            Survey Response Dashboard
          </button>
        </div>
      </div>
      <Panel className="survey-reports-table-panel">
        <h2 className="survey-reports-panel-title">Progress status</h2>
        {loading && schools.length === 0 ? (
          <Loader />
        ) : (
          <CustomTable tableHeaderData={tableHeaderData} tableBodyData={schools} />
        )}
      </Panel>
    </div>
  );
};

export default SurveyReportsOverview;
